import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, CalendarClock, CheckCheck, UserCheck, WifiOff, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import * as apiClient from '@/services/apiClient';

const POLL_INTERVAL = 30_000; // 30 s

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await apiClient.getNotifications();
      // Only company-relevant types for the bell
      const filtered = (res.data || []).filter(n =>
        n.type === 'interview_completed' ||
        n.type === 'interview_terminated' ||
        n.type === 'interview_expired' ||
        n.type === 'session_disconnected' ||
        n.type === 'reschedule_requested'
      );
      setNotifications(filtered.slice(0, 15));
      setUnreadCount(filtered.filter(n => !n.isRead).length);
    } catch {
      // silently fail — don't disrupt the UI
    }
  }, []);

  // Initial fetch + polling
  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  const updatePanelPos = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPanelPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    updatePanelPos();
    window.addEventListener('resize', updatePanelPos);
    window.addEventListener('scroll', updatePanelPos, true);
    return () => {
      window.removeEventListener('resize', updatePanelPos);
      window.removeEventListener('scroll', updatePanelPos, true);
    };
  }, [open, updatePanelPos]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        panelRef.current?.contains(e.target) ||
        buttonRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await apiClient.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (notif) => {
    // Close dropdown immediately
    setOpen(false);

    // Mark as read fire-and-forget (don't block navigation)
    if (!notif.isRead) {
      apiClient.markNotificationRead(notif._id)
        .then(() => {
          setNotifications(prev =>
            prev.map(n => n._id === notif._id ? { ...n, isRead: true } : n)
          );
          setUnreadCount(prev => Math.max(0, prev - 1));
        })
        .catch(() => {});
    }

    // Navigate — to specific process or pipeline list as fallback
    const processId = notif.data?.processId;
    if (processId) {
      navigate(`/admin/candidate-process/${processId}`);
    } else {
      navigate('/admin/candidate-process');
    }
  };

  const dropdownPanel = open && panelPos ? (
    <div
      ref={panelRef}
      style={{ top: panelPos.top, right: panelPos.right }}
      className="fixed z-[100] w-80 rounded-xl border border-border bg-card shadow-xl ring-1 ring-black/5"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Notifications</span>
          {unreadCount > 0 && (
            <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600">
              {unreadCount} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              disabled={loading}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
              title="Mark all as read"
            >
              <CheckCheck className="h-3 w-3" />
              All read
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Notification list */}
      <div className="max-h-[400px] overflow-y-auto overscroll-contain">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
            <Bell className="h-8 w-8 opacity-30" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          notifications.map(notif => (
            <button
              key={notif._id}
              type="button"
              onClick={() => handleClick(notif)}
              className={cn(
                'flex w-full items-start gap-3 border-b border-border/60 px-4 py-3 text-left transition-colors last:border-0',
                'hover:bg-muted/60',
                !notif.isRead && 'bg-primary/5'
              )}
            >
              {/* Icon */}
              <div className={cn(
                'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                notif.type === 'interview_completed'  ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' :
                notif.type === 'interview_terminated' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                notif.type === 'session_disconnected' ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' :
                notif.type === 'reschedule_requested' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' :
                'bg-muted text-muted-foreground'
              )}>
                {notif.type === 'session_disconnected' ? <WifiOff className="h-4 w-4" /> :
                 notif.type === 'reschedule_requested' ? <CalendarClock className="h-4 w-4" /> :
                 <UserCheck className="h-4 w-4" />}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <p className={cn(
                  'text-sm leading-snug',
                  !notif.isRead ? 'font-semibold text-foreground' : 'font-normal text-foreground/80'
                )}>
                  {notif.message}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  {timeAgo(notif.createdAt)}
                  {notif.data?.processId && (
                    <span className="ml-1.5 text-primary">· View process →</span>
                  )}
                </p>
              </div>

              {/* Unread dot */}
              {!notif.isRead && (
                <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
              )}
            </button>
          ))
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
          'text-muted-foreground hover:bg-muted hover:text-foreground',
          open && 'bg-muted text-foreground'
        )}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {dropdownPanel && createPortal(dropdownPanel, document.body)}
    </div>
  );
}
